import React, { useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useHistory } from "react-router-dom";
import isEmail from "validator/lib/isEmail";
import * as yup from "yup";

import { LAST_PAGE, PRIVATE_ROUTES } from "../../constants";
import { UserProvider } from "../../context/UserContext";
import useUserContext from "../../hooks/useUserContext";
import { Timezone } from "../../models/timezone";
import { User } from "../../models/user";
import { UserTypes } from "../../util/enums";
import { handleValidation } from "../../util/formValidation";
import { userScheme } from "../../util/genericSchemas";
import Button, { Color } from "../Form/Button";
import UserForm from "../Generic/SettingsUserForm";
import { AvatarGeneric } from "../Generic/SettingsUserForm/components/AvatarGeneric";
import SkeletonComponent from "../Generic/Skeleton";
import SubHeader from "../Generic/SubHeader";
import UITabMaterial, { PanelProps } from "../Generic/UITabMaterial";
import LoadingModal from "../LoadingModal";
import useStyles from "./styles";
import { UserVersion } from "./UserVersion";
import { useAppState } from "Context/StateContext";
import { isMoving } from "Util/validation";

const ALLOWED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];

function Profile() {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const classes = useStyles();
  const history = useHistory();

  const {
    rest: { user },
    isUpdating,
    fetchingUser,
    timezones,
    handleUpdate,
  } = useUserContext(undefined, true);

  const [newUser, setNewUser] = useState<User>();
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [oldPassword, setOldPassword] = useState<string>("");
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const { genericObject } = userScheme(translate, newUser, newUser);

  const errorMessage = translate("thisFieldIsRequired");

  const schema = yup.object().shape({
    name: yup.string().required(errorMessage).typeError(errorMessage),
    lastName: yup.string().required(errorMessage).typeError(errorMessage),
    phone: yup
      .string()
      .required(errorMessage)
      .typeError(errorMessage)
      .test({
        name: "validate-phone",
        message: translate("pleaseEnterAValidPhoneNumber"),
        test: (value) => {
          return isValidPhoneNumber(value || "");
        },
      }),
    email: yup
      .string()
      .required(errorMessage)
      .typeError(errorMessage)
      .test({
        name: "validate-email",
        message: translate("thisEmailIsInvalid"),
        test: (value) => {
          return isEmail(value || "");
        },
      }),
    username: yup.string().required(errorMessage).typeError(errorMessage),
    code: yup
      .string()
      .nullable()
      .when("isProfileMoving", {
        is: true,
        then: yup
          .string()
          .nullable()
          .test({
            name: "validate-code",
            message: translate("thisFieldIsRequired"),
            test: (value) => {
              if (!value && newUser?.typeUser === UserTypes.SALES) return false;

              return true;
            },
          }),
      }),
    password: yup.string(),
    hasPasswordBeenChanged: yup.boolean(),
    confirmPassword: yup.string().when("hasPasswordBeenChanged", {
      is: true,
      then: yup.string().test(
        "password-matches",
        translate("passwordsDoNotMatch"),
        // eslint-disable-next-line func-names
        function (value) {
          return this.parent.password === value;
        }
      ),
    }),
    timezoneId: yup.number().required(errorMessage).typeError(errorMessage),
    isProfileMoving: yup.boolean(),
  });

  const setValidationSchema = (userValidation?: User) => ({
    ...genericObject(userValidation),
    hasPasswordBeenChanged:
      userValidation?.password && userValidation?.password?.length > 0
        ? userValidation?.password !== oldPassword
        : false,
    password: userValidation?.password,
    confirmPassword: userValidation?.confirmPassword,
  });

  const handleSubmit = () => {
    setHasSubmitted(true);
    let copyUser = newUser && { ...newUser };

    if (copyUser && !copyUser?.timezoneId) {
      copyUser = setTimezoneValue(copyUser);
    }

    if (copyUser?.password?.length === 0) copyUser = { ...copyUser, password: oldPassword };

    const validateSyncObject = setValidationSchema(copyUser);

    if (
      handleValidation({
        schema,
        validateSyncObject,
        updateErrors: setErrors,
      })
    ) {
      let clonedUser = { ...copyUser };
      const { password } = clonedUser;

      if (password) {
        let changedPassword = password;

        if (password !== oldPassword) changedPassword = password;
        clonedUser = { ...clonedUser, password: changedPassword };
      }

      handleUpdate(clonedUser as User);
    }
  };

  const details: PanelProps = {
    menuItem: translate("details"),
    className: classes.uiTab,
    key: "details",
    render: () => (
      <div>
        <section className={classes.avatarWrapper}>
          <label className={classes.label}>{translate("avatar")}</label>
          <SkeletonComponent isLoading={fetchingUser && !hasSubmitted}>
            <Grid item xs={12} sm={12} md={2} lg={2}>
              <AvatarGeneric
                isOnProfilePage
                allowedImageExtensions={ALLOWED_IMAGE_EXTENSIONS}
                name="picture"
                data-cy="avatar"
                picture={newUser?.picture?.mediaLink}
                uploadMode="s3"
                onChangePicture={(value) => {
                  setNewUser((prev) => {
                    const hasExistingPicture = !!prev?.picture?.mediaLink;
                    return {
                      ...prev,
                      picture: value
                        ? hasExistingPicture
                          ? { ...prev?.picture, mediaLink: value }
                          : { mediaLink: value }
                        : undefined,
                    } as User;
                  });
                }}
              />
            </Grid>
          </SkeletonComponent>
        </section>
        <UserForm
          userInformation={newUser}
          updateOrCreate={(userInfo) => {
            setNewUser({ ...userInfo });
          }}
          isOnProfilePage
          errors={errors}
          isLoading={fetchingUser && !hasSubmitted}
        />
        <section className={classes.buttonsWrapper}>
          <Button
            data-cy="button-cancel"
            customColor={Color.CANCEL}
            onClick={() =>
              history.push(window.localStorage.getItem(LAST_PAGE) || PRIVATE_ROUTES.OVERVIEW)
            }
          >
            {translate("cancel")}
          </Button>
          <Button
            data-cy="button-submit"
            onClick={() => handleSubmit()}
            customColor={Color.SUCCESS}
            disabled={isUpdating}
          >
            {translate("saveChanges")}
          </Button>
        </section>
        <LoadingModal showing={isUpdating} />
      </div>
    ),
  };

  const setTimezoneValue = (clonedUser: User) => {
    const currentTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneFound: Timezone | undefined = timezones?.find(
      (timezone) => timezone.name === currentTimezone
    );

    if (timezoneFound) return { ...clonedUser, timezoneId: timezoneFound.id };

    return clonedUser;
  };

  useEffect(() => {
    if (user) {
      setNewUser(user);
      if (user?.password) setOldPassword(user?.password);
    }
  }, [user]);

  return (
    <>
      {!isMoving(currentUser?.company) && (
        <SubHeader title={translate("profile")}>
          <UserVersion user={user} />
        </SubHeader>
      )}

      <section className={classes.tabWrapper}>
        <UITabMaterial leftPanels={[details]} leftDataCy="details-profile-tabs" />
      </section>
    </>
  );
}

export default function UserProfile() {
  return (
    <UserProvider>
      <Profile />
    </UserProvider>
  );
}
